<template>
  <f7-page name="kyc-organization">
    <f7-navbar :title="$t('kyc.organizationTitle')" back-link no-shadow />

    <AppLayout>
      <template #main>
        <f7-block v-if="groupId">
          <KycStatusBadge :status="kycStatus" />
        </f7-block>

        <f7-block-title v-if="!isDone">
          {{ $t("kyc.organizationSubtitle") }}
        </f7-block-title>

        <f7-block v-if="!isDone" class="kyc-form-block no-margin no-padding">
          <p class="kyc-intro">{{ $t("kyc.organizationIntro") }}</p>

          <f7-list media-list inset  dividers strong>
            <f7-list-input
              :label="$t('kyc.legalName')"
              type="text"
              :placeholder="$t('kyc.legalNamePh')"
              v-model="form.legalName"
              required />
            <f7-list-item :title="$t('kyc.legalForm')" smart-select :smart-select-params="{ openIn: isSmallScreen ? 'sheet' : 'popover', closeOnSelect: true, swipeToClose: true }">
                <select name="legalform" v-model="form.legalForm">
                  <option value="ASSOCIATION">{{ $t("kyc.legalFormAssociation") }}</option>
                  <option value="NGO">{{ $t("kyc.legalFormNgo") }}</option>
                  <option value="COOPERATIVE">{{ $t("kyc.legalFormCooperative") }}</option>
                  <option value="COMPANY">{{ $t("kyc.legalFormCompany") }}</option>
                  <option value="SOLE_TRADER">{{ $t("kyc.legalFormSoleTrader") }}</option>
                  <option value="FOUNDATION">{{ $t("kyc.legalFormFoundation") }}</option>
                </select>
            </f7-list-item>
            <f7-list-input
              :label="$t('kyc.registrationNumber')"
              type="text"
              :placeholder="$t('kyc.registrationNumberPh')"
              v-model="form.registrationNumber"
              required />

            <f7-list-item :title="$t('kyc.registeredCountry')" smart-select
              :smart-select-params="smartSelectParams">
              <select v-model="form.registeredCountry">
                <optgroup
                  v-for="(countryList, continentName, index) in countries"
                  :label="continentName"
                  :key="index">
                  <option
                    v-for="country in countryList"
                    :key="country.id"
                    :value="country.countryCode2"
                    :data-option-image="`https://flagcdn.com/${country.countryCode2.toLowerCase()}.svg`"
                    width="24">
                    {{ country.name }}
                  </option>
                </optgroup>
              </select>
              <template #media>
                <img
                  v-if="groupCountry"
                  :src="`https://flagcdn.com/${groupCountry.countryCode2.toLowerCase()}.svg`"
                  width="24"
                  class="country-flag" />
              </template>
            </f7-list-item>

            <f7-list-input
              :label="$t('kyc.taxIdentifier')"
              type="text"
              :placeholder="$t('kyc.taxIdentifierPh')"
              v-model="form.taxIdentifier" />
            <f7-list-input
              ref="phoneInputRef"
              :label="$t('kyc.phoneNumber')"
              type="tel"
              :placeholder="$t('kyc.phoneNumber')"
              v-model="form.phoneNumber" />
            <f7-list-input
              :label="$t('kyc.email')"
              type="email"
              :placeholder="$t('kyc.emailPh')"
              v-model="form.email" />
          </f7-list>

          <f7-block-title>{{ $t("kyc.beneficialOwner") }}</f7-block-title>
          <f7-list inset strong dividers media-list>
            <f7-list-input
              :label="$t('kyc.beneficialOwnerName')"
              type="text"
              v-model="form.beneficialOwnerName" />
            <f7-list-item :title="$t('kyc.beneficialOwnerIdType')" smart-select :smart-select-params="{ openIn: isSmallScreen ? 'sheet' : 'popover', closeOnSelect: true, swipeToClose: true }">
                <select name="beneficialOwnerIdType" v-model="form.beneficialOwnerIdType">
                  <option value="NATIONAL_ID">{{ $t("kyc.idTypeNationalId") }}</option>
                  <option value="PASSPORT">{{ $t("kyc.idTypePassport") }}</option>
                  <option value="DRIVERS_LICENSE">{{ $t("kyc.idTypeDriversLicense") }}</option>
                   <option value="RESIDENCE_PERMIT">
                  {{ $t("kyc.idTypeResidencePermit") }}
                </option>
                <option value="VOTER_CARD">
                  {{ $t("kyc.idTypeVoterCard") }}
                </option>
                </select>
            </f7-list-item>
            <f7-list-input
              :label="$t('kyc.beneficialOwnerIdNumber')"
              type="text"
              v-model="form.beneficialOwnerIdNumber" />
          </f7-list>

          <f7-list inset strong dividers media-list>
            <f7-list-item
              :title="$t('kyc.selfDeclared')"
              :subtitle="$t('kyc.selfDeclaredHint')">
              <template #after>
                <f7-toggle v-model="form.selfDeclared" />
              </template>
            </f7-list-item>
            <f7-list-input
              v-if="form.selfDeclared"
              type="textarea"
              :label="$t('kyc.authorizationDescription')"
              v-model="form.authorizationDescription"
              :placeholder="$t('kyc.authorizationDescriptionPh')" />
          </f7-list>

          <f7-button large raised round fill @click="submit" :disabled="!canSubmit || submitting">
            {{ submitting ? $t("common.loading") : $t("kyc.submitVerification") }}
          </f7-button>

          <p class="kyc-note">{{ $t("kyc.organizationNote") }}</p>
        </f7-block>

        <f7-block v-if="isVerified" class="kyc-success">
          <f7-icon f7="checkmark_circle_fill" size="48" color="green" />
          <h3>{{ $t("kyc.verifiedTitle") }}</h3>
          <p>{{ $t("kyc.verifiedMessageOrg") }}</p>
        </f7-block>

        <f7-block v-if="isRejected && !isDone" class="kyc-rejected">
          <f7-icon f7="xmark_circle_fill" size="48" color="red" />
          <h3>{{ $t("kyc.rejectedTitle") }}</h3>
          <p>{{ kycStatus?.rejectionReason || $t("kyc.rejectedDefault") }}</p>
          <f7-button fill round raised @click="resubmit">{{ $t("kyc.resubmit") }}</f7-button>
        </f7-block>

        <!-- Document Upload Section -->
        <template v-if="!isDone">
          <f7-block-title>{{ $t('kyc.uploadDocumentTitle') }}</f7-block-title>
          <f7-block>
            <p class="kyc-doc-intro">{{ $t('kyc.uploadDocumentIntro') }}</p>
            <KycDocumentCapture
              v-for="docDef in requiredDocumentTypes"
              :key="docDef.type"
              :groupId="groupId"
              :documentType="docDef"
              :issuingCountry="form.registeredCountry"
              @uploaded="handleDocUploaded" />
          </f7-block>
        </template>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { reactive, ref, computed, onMounted, watch } from 'vue'
import _ from 'lodash'
import { f7 } from 'framework7-vue'
import { useKycStore } from '../js/pinia-stores/kyc.js'
import { useKeycloakStore } from '../js/pinia-stores/keycloak.js'
import { useLocationStore } from '../js/pinia-stores/location.js'
import { storeToRefs } from 'pinia'
import i18n from '../js/i18n.js'
import AppLayout from '../components/common/AppLayout.vue'
import KycStatusBadge from '../components/kyc/KycStatusBadge.vue'
import KycDocumentCapture from '../components/kyc/KycDocumentCapture.vue'
import { useTelephone } from '../js/composables/useTelephone.js'
import { useSmallScreen } from '../js/composables/useMediaQuery.js'

const isSmallScreen = useSmallScreen()

const { t } = i18n.global
const props = defineProps({ f7route: Object, f7router: Object })

const kycStore = useKycStore()
const {
  kycStatus, loading, isVerified, isRejected,
  submitOrganizationKyc, fetchKycStatus, fetchLegalProfile, fetchDocuments,
} = storeToRefs(kycStore)
const keycloak = useKeycloakStore()
const locationStore = useLocationStore()
const groupId = props.f7route?.params?.groupId || null
const submitting = ref(false)
const isDone = ref(false)
const phoneInputRef = ref(null)
useTelephone(phoneInputRef)

const country = computed(() => locationStore.country)
const selectedCountry = ref(null)

if (keycloak.authenticated) {
  selectedCountry.value = keycloak?.profile?.countryCode || null
}

const countries = computed(() => {
  return Array.isArray(country.value)
    ? _.groupBy(country.value, "continentName")
    : {}
})

const groupCountry = computed(() => {
  if (!selectedCountry.value) return null
  const countryList = locationStore.country || []
  return countryList.find(c => c.countryCode2 === selectedCountry.value)
})

// Ensure selectedCountry is set from keycloak profile when location store loads
watch(
  () => locationStore.country,
  (countries) => {
    if (
      countries?.length &&
      !selectedCountry.value &&
      keycloak.profile?.countryCode
    ) {
      const match = countries.find(
        (c) => c.countryCode2 === keycloak.profile.countryCode,
      )
      if (match) {
        selectedCountry.value = match.countryCode2
      }
    }
  },
  { immediate: true },
)

// Initialize registeredCountry from selectedCountry
const form = reactive({
  legalName: '',
  legalForm: 'ASSOCIATION',
  registrationNumber: '',
  registeredCountry: selectedCountry.value || 'CI',
  taxIdentifier: '',
  phoneNumber: '',
  email: '',
  website: '',
  beneficialOwnerName: '',
  beneficialOwnerIdType: 'NATIONAL_ID',
  beneficialOwnerIdNumber: '',
  selfDeclared: false,
  authorizationDescription: '',
})

const smartSelectParams = computed(() => ({
  openIn: 'popup',
  searchbar: true,
  closeOnSelect: true,
  tabletFullScreen: false,
  swipeToClose: true,
  searchbarPlaceholder: t('common.search'),
}))

const LEGAL_FORM_DOC_TYPE_MAP = {
  ASSOCIATION: 'ASSOCIATION_REGISTRATION',
  NGO:        'ASSOCIATION_REGISTRATION',
  COOPERATIVE: 'BUSINESS_REGISTRATION',
  COMPANY:    'COMPANY_REGISTRATION',
  SOLE_TRADER:'BUSINESS_REGISTRATION',
  FOUNDATION: 'BUSINESS_REGISTRATION',
}

const DOC_TYPE_LABEL = {
  BUSINESS_REGISTRATION:  'kyc.businessRegistrationLabel',
  ASSOCIATION_REGISTRATION:'kyc.associationRegistrationLabel',
  TAX_CERTIFICATE:        'kyc.taxCertificateLabel',
  PROOF_OF_ADDRESS:       'kyc.proofOfAddressLabel',
  AUTHORIZATION_RESOLUTION:'kyc.authorizationLetterLabel',
}

const canSubmit = computed(() => {
  return form.legalName.trim() && form.registrationNumber.trim()
})

const requiredDocumentTypes = computed(() => {
  const regType = LEGAL_FORM_DOC_TYPE_MAP[form.legalForm] || 'BUSINESS_REGISTRATION'
  const docs = [{
    type: regType,
    label: t(DOC_TYPE_LABEL[regType]) || regType,
    description: '',
    frontSide: false,
  }, {
    type: 'TAX_CERTIFICATE',
    label: t(DOC_TYPE_LABEL.TAX_CERTIFICATE) || 'Tax Certificate',
    description: '',
    frontSide: false,
  }, {
    type: 'PROOF_OF_ADDRESS',
    label: t(DOC_TYPE_LABEL.PROOF_OF_ADDRESS) || 'Proof of address',
    description: '',
    frontSide: false,
  }]
  if (!form.selfDeclared) {
    docs.push({
      type: 'AUTHORIZATION_RESOLUTION',
      label: t(DOC_TYPE_LABEL.AUTHORIZATION_RESOLUTION) || 'Authorization letter',
      description: '',
      frontSide: false,
    })
  }
  return docs
})

function handleDocUploaded(doc) {
  const idx = kycStore.kycDocuments.value.findIndex(d => d.id === doc.id)
  if (idx >= 0) kycStore.kycDocuments.value[idx] = doc
  else kycStore.kycDocuments.value.push(doc)
}

function resubmit() {
  isDone.value = false
}

onMounted(() => {
  if (groupId) {
    kycStore.fetchKycStatus(groupId).then(() => {
      if (kycStatus.value) {
        isDone.value = kycStatus.value.status === 'VERIFIED' || kycStatus.value.status === 'REJECTED'
        if (kycStatus.value.legalName) form.legalName = kycStatus.value.legalName
        if (kycStatus.value.registrationNumber) form.registrationNumber = kycStatus.value.registrationNumber
        if (kycStatus.value.registeredCountry) form.registeredCountry = kycStatus.value.registeredCountry
      }
      kycStore.fetchLegalProfile(groupId).then(profile => {
        if (profile) {
          if (profile.legalName) form.legalName = profile.legalName
          if (profile.legalForm) form.legalForm = profile.legalForm
          if (profile.registrationNumber) form.registrationNumber = profile.registrationNumber
          if (profile.registeredCountry) form.registeredCountry = profile.registeredCountry
          if (profile.taxIdentifier) form.taxIdentifier = profile.taxIdentifier
          if (profile.phoneNumber) form.phoneNumber = profile.phoneNumber
          if (profile.email) form.email = profile.email
          if (profile.beneficialOwnerName) form.beneficialOwnerName = profile.beneficialOwnerName
          if (profile.selfDeclared) form.selfDeclared = profile.selfDeclared
          if (profile.authorizationDescription) form.authorizationDescription = profile.authorizationDescription
        }
      }).catch(() => {})
      kycStore.fetchDocuments(groupId).catch(() => {})
    }).catch(() => {})
  }
})

async function submit() {
  submitting.value = true
  try {
    const payload = {
      groupId,
      legalName: form.legalName,
      legalForm: form.legalForm,
      registrationNumber: form.registrationNumber,
      registeredCountry: form.registeredCountry,
      taxIdentifier: form.taxIdentifier || null,
      phoneNumber: form.phoneNumber || null,
      email: form.email || null,
      website: form.website || null,
      beneficialOwnerName: form.beneficialOwnerName || null,
      beneficialOwnerIdType: form.beneficialOwnerIdType || null,
      beneficialOwnerIdNumber: form.beneficialOwnerIdNumber || null,
      selfDeclared: form.selfDeclared,
      authorizationDescription: form.authorizationDescription || null,
    }
    await kycStore.submitOrganizationKyc(payload)
    f7.toast.show({ text: t('kyc.submittedToast'), closeTimeout: 2000 })
    isDone.value = true
  } catch (e) {
    f7.dialog.alert(e?.response?.data?.message || e?.message || t('kyc.submitFailed'))
  } finally {
    submitting.value = false
  }
}
</script>